import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { GlowButton } from '@/components/brand/GlowButton';

// Dark cinematic London-at-dusk photograph (Unsplash editorial, royalty-free).
// Loaded eagerly because this is the LCP element and the page's hero.
const HERO_IMAGE =
    'https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=2400&q=85';

export const Hero = () => {
    return (
        <section
            data-testid="home-hero-section"
            className="relative isolate overflow-hidden min-h-[88vh] flex items-center bg-la-canvas"
        >
            {/* Cinematic background image */}
            <img
                src={HERO_IMAGE}
                alt=""
                aria-hidden
                fetchPriority="high"
                loading="eager"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover object-center"
                style={{ filter: 'saturate(0.78) brightness(0.55) contrast(1.05)' }}
            />

            {/* Dark luxury overlay — keeps text fully legible (~78% opacity) */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'linear-gradient(180deg, rgba(6,8,12,0.62) 0%, rgba(6,8,12,0.78) 55%, rgba(6,8,12,0.92) 100%)',
                }}
            />

            {/* Subtle gold radial halo behind the headline */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(900px 360px at 35% 38%, rgba(201,168,106,0.10), transparent 65%)',
                }}
            />

            {/* Film-grain noise overlay for tactile depth */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-[0.045] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0'/></filter><rect width='160' height='160' filter='url(%23n)'/></svg>\")",
                    backgroundSize: '160px 160px',
                }}
            />

            <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-40 sm:py-44 lg:py-48">
                <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-la-gold/25 bg-la-canvas/50 backdrop-blur-sm text-[10.5px] tracking-[0.28em] uppercase text-la-text2 font-sans">
                        <span className="h-1 w-1 rounded-full bg-la-gold shadow-[0_0_8px_rgba(201,168,106,0.85)]" />
                        London · Private Consultancy
                    </div>
                    <h1
                        data-testid="home-hero-headline"
                        className="mt-8 font-display font-normal text-[44px] sm:text-[60px] lg:text-[74px] leading-[1.04] tracking-[-0.005em] text-la-text text-balance"
                        style={{ textShadow: '0 1px 32px rgba(0,0,0,0.45)' }}
                    >
                        Strategic Consultancy{' '}
                        <span className="sm:block">for Business, Property, Trade <span className="italic text-la-gold">&amp;</span> Private Clients.</span>
                    </h1>
                    <p className="mt-7 max-w-2xl text-[15.5px] sm:text-[17px] leading-relaxed text-la-text2 font-sans">
                        A discreet London-based advisory practice serving business owners, investors, property clients and international trade clients across complex, cross-border matters.
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-3">
                        <GlowButton
                            as={Link}
                            to="/contact"
                            size="lg"
                            variant="gold"
                            data-testid="home-hero-book-button"
                        >
                            Request Consultation <ArrowRight className="h-4 w-4" />
                        </GlowButton>
                        <GlowButton
                            as={Link}
                            to="/payments"
                            size="lg"
                            variant="ghost"
                            data-testid="home-hero-pay-button"
                        >
                            Make Payment
                        </GlowButton>
                    </div>
                </div>
            </div>

            {/* Bottom hairline that fades into the page below — the imagery and the next section meet cleanly */}
            <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-px"
                style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(201,168,106,0.30) 50%, transparent 100%)' }}
            />
        </section>
    );
};

export default Hero;
